import React, {
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";

import { Guid } from "@/types";
import Button from "components/Button/Button";
import toastMessages from "stores/toastMessages";
import { TableColumn, TableRow } from "components/Table/Table";
import AssetInvoices from "components/AssetInvoices/AssetInvoices";
import invoiceSidebars from "stores/invoiceSidebars";
import { getInvoiceCategoryTypeShortLabel } from "utils/utils";
import usePagedData from "hooks/usePagedData";
import FinanceService, {
    IGetAssetIncomingInvoicesParams,
} from "services/FinanceService";
import AssetInvoiceIncoming, {
    InvoiceCategoryType,
} from "models/AssetInvoiceIncoming";
import Select, { ISelectOption, } from "components/Select/Select";
import Text from "components/Text/Text";
import { Flex, FlexItem } from "components/Flex";
import Highlighter from "components/Highlighter/Highlighter";
import Tooltip from "components/Tooltip/Tooltip";
import AmountWithCurrency from "models/AmountWithCurrency";
import CostCategory from "models/CostCategory";
import useFinanceSettings from "hooks/useFinanceSettings";
import financeSettingsStore from "stores/financeSettings";

interface IAssetInvoicesIncomingProps {
    assetId: Guid;
    scrollEl?: React.RefObject<HTMLDivElement | null>;
}

type OptionValue = InvoiceCategoryType | 0 | CostCategory

enum SelectGroup {
    type = "type",
    costCategory = "costCategory"
}

const AssetInvoicesIncoming: FunctionComponent<
    React.PropsWithChildren<IAssetInvoicesIncomingProps>
> = ({ assetId, scrollEl }) => {
    const { t } = useTranslation();
    const [selectedFilter, setSelectedFilter] = useState<OptionValue>(0);
    const { costCategories } = useFinanceSettings()
    const invoicesParams = useMemo<IGetAssetIncomingInvoicesParams>(() => {
        const type = (selectedFilter instanceof CostCategory || !selectedFilter) ? undefined : selectedFilter;
        const costCategoryId = selectedFilter instanceof CostCategory ? selectedFilter.costCategoryId : undefined;
        return {
            assetId,
            pageSize: 30,
            type,
            costCategoryId
        };
    }, [assetId, selectedFilter]);

    const invoicesCall = useCallback(() => {
        return FinanceService.getAssetIncomingInvoices(invoicesParams);
    }, [invoicesParams]);

    const {
        items: invoices,
        loadNextPage,
        isInitialLoad,
        isLoading,
        hasMore,
        aggregate
    } = usePagedData<AssetInvoiceIncoming, { totalAmount?: AmountWithCurrency }>(
        invoicesCall,
        toastMessages.showSystemError
    );

    const getSelectGroupHeader = (group: SelectGroup) => {
        switch (group) {
            case SelectGroup.type:
                return t("common.type")
            case SelectGroup.costCategory:
                return t("invoices.costCategory")
        }
    }

    function filterOptionTemplate(option: ISelectOption<CostCategory | InvoiceCategoryType | 0>, search: string) {

        if (option.value instanceof CostCategory) {
            return <Flex justifyContent='space-between'>
                <FlexItem className='ellipsis max-w-40'>
                    <Highlighter text={option.value.costCategoryLedgerAccount || t("common.noValue")} highlight={search} />
                    { }
                </FlexItem>
                <FlexItem className="ellipsis color-grey-500 pl-1 max-w-60">
                    <Highlighter text={option.value.costCategoryName} highlight={search} />
                </FlexItem>
            </Flex>
        } else { return <Highlighter text={option.label} highlight={search} /> }
    }

    useEffect(() => {
        if (!costCategories)
            financeSettingsStore.fetchCostCategories()
    }, [costCategories])

    const tableColumns: TableColumn[] = [
        { title: t("invoices.invoiceDate"), width: "20%" },
        { title: t("invoices.invoiceNo"), width: "20%" },
        {
            title: t("common.type"),
            width: "33%",
            explanation: {
                content: (
                    <ul>
                        <li>{t("invoices.typeTooltip.item1")}</li>
                        <li>{t("invoices.typeTooltip.item2")}</li>
                    </ul>
                ),
            },
        },
        { title: t("asset.assignedAmount"), width: "27%" },
    ];

    const tableRows = useMemo(() => {
        if (!invoices) return null;

        const rows: TableRow[] = [];

        invoices.forEach((invoice) => {
            const row: TableRow = [
                invoice.invoiceDate.format(t("config.dateLongFormat")),
                <Button
                    theme="link"
                    label={invoice.invoiceNumber}
                    onClick={() =>
                        invoice.linkGetInvoiceDetails &&
                        invoiceSidebars.show({
                            invoiceId: invoice.id,
                            getInvoiceDetailsLink:
                                invoice.linkGetInvoiceDetails,
                        })
                    }
                    userSelect="text"
                    clamp={1}
                />,
                <Flex flow={"column"}>
                    <Text>
                        {getInvoiceCategoryTypeShortLabel(invoice.invoiceType)}
                    </Text>
                    <Text className="color-grey-500">{invoice.costCategory}</Text>
                    {invoice.ledgerAccount && <Text className="color-grey-500">({invoice.ledgerAccount})</Text>}
                </Flex>,
                <Flex flow="column" alignItems={"flex-end"}>
                    <Text>{invoice.assignedAmount.print()}</Text>
                    {invoice.assignedForeignCurrencyAmount && <Text type="pSmallest" className="color-grey-500" >
                        {invoice.assignedForeignCurrencyAmount.print()}
                    </Text>}
                    {invoice.referenceText && <Tooltip content={invoice.referenceText}><Button theme="link" label={t("invoices.referenceText")} /></Tooltip>}
                </Flex>,
            ];
            rows.push(row);
        });

        return rows;
    }, [invoices, t]);

    const filterSelectOptions = useMemo(() => {
        const options: ISelectOption<OptionValue>[] = [
            {
                label: t("invoices.allTypes"),
                value: 0,
                group: SelectGroup.type
            },
        ];

        for (let key in InvoiceCategoryType) {
            options.push({
                label: getInvoiceCategoryTypeShortLabel(
                    InvoiceCategoryType[key]
                ),
                value: InvoiceCategoryType[key],
                group: SelectGroup.type
            });
        }
        options.push({
            label: t("common.allCategories"),
            value: 0,
            group: SelectGroup.costCategory
        },)
        options.push(...costCategories?.list.map(cc => ({
            label: cc.costCategoryLedgerAccount ? `${cc.costCategoryLedgerAccount} (${cc.costCategoryName})` : cc.costCategoryName,
            value: cc,
            group: SelectGroup.costCategory
        })) ?? [])

        return options;
    }, [costCategories?.list, t]);

    return (
        <>
            <Text type="h3" className="mb-3">
                {t("asset.incomingInvoicesTitle")}
            </Text>
            <Flex justifyContent="space-between" className="mb-8">
                <Flex flow='column'>
                    <Text className="mb-2">{t("common.type")}</Text>
                    <Select
                        value={selectedFilter}
                        options={filterSelectOptions}
                        onChange={(option) =>
                            setSelectedFilter(option?.value || 0)
                        }
                        optionTemplate={filterOptionTemplate}
                        groupOptions={options => {
                            return Object.values(SelectGroup).map(group => (
                                {
                                    label: getSelectGroupHeader(group),
                                    options: options.filter(option => option.group === group),
                                }
                            ))
                        }}
                    />
                </Flex>

                {aggregate?.totalAmount &&
                    <Flex flow='column'>
                        <Text className="mb-3">{t("common.totalAmount")}</Text>
                        <Text bold>{aggregate.totalAmount.print()}</Text>
                    </Flex>}
            </Flex>
            <AssetInvoices
                tableColumns={tableColumns}
                tableRows={tableRows}
                loadNextPage={loadNextPage}
                isInitialLoad={isInitialLoad}
                isLoading={isLoading}
                hasMore={hasMore}
                scrollEl={scrollEl}
            />
        </>
    );
};

export default AssetInvoicesIncoming;
